import { useRef, useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import {
  Bold, Italic, List, ListOrdered, Quote, Link2, Image as ImageIcon,
  Video, Heading1, Heading2, Eraser, Loader2, LayoutTemplate, Plus, Trash2, X,
  Underline, AlignLeft, AlignCenter, AlignRight, Palette,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Reusable rich-text editor. Stores sanitized HTML (rendered elsewhere via DOMPurify).
// Toolbar: headings, bold/italic, lists, quote, link, inline image upload, video embed.
const SANITIZE_OPTS = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "src", "controls", "target"],
};

// Only these hosts may appear as an <iframe src>. Any other iframe is stripped on sanitize,
// so pasted/stored HTML cannot embed arbitrary third-party frames (clickjacking/phishing).
const ALLOWED_IFRAME_HOSTS = ["www.youtube.com", "youtube.com", "www.youtube-nocookie.com", "player.vimeo.com"];
let _iframeHookAdded = false;
function ensureIframeHook() {
  if (_iframeHookAdded) return;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "IFRAME") {
      let ok = false;
      try {
        const u = new URL(node.getAttribute("src") || "", window.location.origin);
        ok = u.protocol === "https:" && ALLOWED_IFRAME_HOSTS.includes(u.hostname) &&
          (u.hostname.includes("youtube") ? u.pathname.startsWith("/embed/") : u.pathname.startsWith("/video/"));
      } catch { ok = false; }
      if (!ok && node.parentNode) node.parentNode.removeChild(node);
    }
  });
  _iframeHookAdded = true;
}

export function sanitizeHtml(html) {
  ensureIframeHook();
  return DOMPurify.sanitize(html || "", SANITIZE_OPTS);
}

const BRAND_COLORS = [
  ["Ink", "#1A242B"], ["Navy", "#144F75"], ["Sage", "#5C8A72"], ["Terracotta", "#D98E73"], ["Clay", "#B5794E"], ["Mist", "#8FA89B"],
];

const escapeHtml = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const isHtml = (s) => /<\/?(p|br|b|strong|i|em|u|h[1-6]|ul|ol|li|a|blockquote|div|span|font|img|iframe|video|figure)\b[^>]*>/i.test(s || "");
// Legacy plain text is shown in the editor with its line breaks; it is only converted if the user edits it.
const toEditorHtml = (v) => (!v ? "" : isHtml(v) ? v : escapeHtml(v).split(/\n{2,}/).map((b) => `<p>${b.replace(/\n/g, "<br>")}</p>`).join(""));

function embedFromUrl(raw) {
  const url = (raw || "").trim();
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `<div class="rte-video"><iframe src="https://www.youtube.com/embed/${yt[1]}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `<div class="rte-video"><iframe src="https://player.vimeo.com/video/${vm[1]}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`;
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) return `<div class="rte-video"><video src="${url}" controls></video></div>`;
  return "";
}

export default function RichTextEditor({ value, onChange, placeholder = "Write something…", testid = "rich-text-editor", minHeight = 180, enableBlocks = true, media = true }) {
  const ref = useRef(null);
  const lastValue = useRef(null);
  const dirty = useRef(false);
  const [showColors, setShowColors] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showBlocks, setShowBlocks] = useState(false);
  const qc = useQueryClient();

  const { data: blocks = [], isLoading: blocksLoading, isError: blocksError } = useQuery({
    queryKey: ["content-blocks"],
    queryFn: () => api.get("/content-blocks").then((r) => r.data),
    enabled: enableBlocks && showBlocks,
    retry: false,
  });

  // Load incoming HTML only when it differs (avoid clobbering the caret while typing).
  useEffect(() => {
    if (ref.current && (value || "") !== lastValue.current) {
      ref.current.innerHTML = toEditorHtml(value || "");
      lastValue.current = value || "";
      dirty.current = false;
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current || !dirty.current) return;
    const html = sanitizeHtml(ref.current.innerHTML);
    lastValue.current = html;
    onChange(html);
  }, [onChange]);

  const exec = (cmd, arg = null, css = false) => {
    ref.current?.focus();
    document.execCommand("styleWithCSS", false, css);
    document.execCommand(cmd, false, arg);
    dirty.current = true;
    emit();
  };

  const insertHtml = (html) => {
    ref.current?.focus();
    document.execCommand("insertHTML", false, html);
    dirty.current = true;
    emit();
  };

  const addLink = () => {
    let url = (window.prompt("Link URL (e.g. https://example.com)") || "").trim();
    if (!url) return;
    if (!/^(https?:|mailto:|tel:|\/)/i.test(url)) url = `https://${url}`;
    exec("createLink", url);
  };

  const addVideo = () => {
    const url = window.prompt("Paste a YouTube, Vimeo, or MP4 video URL");
    if (!url) return;
    const html = embedFromUrl(url);
    if (!html) return toast.error("Couldn't recognize that video URL.");
    insertHtml(html + "<p><br/></p>");
  };

  const pickImage = () => fileRef.current?.click();
  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      insertHtml(`<img src="${data.url}" alt="" class="rte-img"/><p><br/></p>`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const insertBlock = (b) => {
    insertHtml((b.html || "") + "<p><br/></p>");
    setShowBlocks(false);
  };

  const saveAsBlock = async () => {
    const html = sanitizeHtml(ref.current?.innerHTML || "");
    if (!html.trim()) return toast.error("Nothing to save yet — write something first.");
    const name = window.prompt("Save this content as a reusable block. Name it:");
    if (!name) return;
    try {
      await api.post("/admin/content-blocks", { name, html, kind: "section" });
      qc.invalidateQueries({ queryKey: ["content-blocks"] });
      toast.success(`Saved block "${name}"`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Only admins can save blocks."); }
  };

  const deleteBlock = async (id) => {
    try {
      await api.delete(`/admin/content-blocks/${id}`);
      qc.invalidateQueries({ queryKey: ["content-blocks"] });
    } catch (e) { toast.error("Couldn't delete block"); }
  };

  const Btn = ({ onClick, title, tid, children }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      data-testid={`${testid}-${tid}`}
      aria-label={title}
      className="h-10 w-10 sm:h-8 sm:w-8 grid place-items-center rounded-md text-[#4A5D6B] hover:bg-[#E9E4DA] hover:text-[#144F75] transition-colors">
      {children}
    </button>
  );

  return (
    <div data-testid={testid} className="rte border border-[#D5CEC2] rounded-xl bg-white">
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 border-b border-[#E5E0D8] bg-[#F9F7F3] rounded-t-xl">
        <Btn onClick={() => exec("formatBlock", "<h2>")} title="Heading" tid="h1"><Heading1 size={16} /></Btn>
        <Btn onClick={() => exec("formatBlock", "<h3>")} title="Subheading" tid="h2"><Heading2 size={16} /></Btn>
        <Btn onClick={() => exec("bold")} title="Bold" tid="bold"><Bold size={16} /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic" tid="italic"><Italic size={16} /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline" tid="underline"><Underline size={16} /></Btn>
        <span className="relative">
          <Btn onClick={() => setShowColors((v) => !v)} title="Text colour" tid="color"><Palette size={16} /></Btn>
          {showColors && (
            <span className="absolute left-0 top-full z-20 mt-1 grid grid-cols-3 gap-1.5 w-max rounded-xl border border-[#E5E0D8] bg-white p-2 shadow-lg" data-testid={`${testid}-color-menu`}>
              {BRAND_COLORS.map(([name, hex]) => (
                <button key={hex} type="button" title={name} aria-label={`${name} text`} onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { exec("foreColor", hex, true); setShowColors(false); }} data-testid={`${testid}-color-${name.toLowerCase()}`}
                  className="h-8 w-8 rounded-full border border-black/10" style={{ background: hex }} />
              ))}
            </span>
          )}
        </span>
        <span className="w-px h-5 bg-[#E5E0D8] mx-1" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Bullet list" tid="ul"><List size={16} /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list" tid="ol"><ListOrdered size={16} /></Btn>
        <Btn onClick={() => exec("formatBlock", "<blockquote>")} title="Quote" tid="quote"><Quote size={16} /></Btn>
        <Btn onClick={addLink} title="Link" tid="link"><Link2 size={16} /></Btn>
        <span className="w-px h-5 bg-[#E5E0D8] mx-1" />
        <Btn onClick={() => exec("justifyLeft", null, true)} title="Align left" tid="align-left"><AlignLeft size={16} /></Btn>
        <Btn onClick={() => exec("justifyCenter", null, true)} title="Align centre" tid="align-center"><AlignCenter size={16} /></Btn>
        <Btn onClick={() => exec("justifyRight", null, true)} title="Align right" tid="align-right"><AlignRight size={16} /></Btn>
        {media && (
          <>
            <span className="w-px h-5 bg-[#E5E0D8] mx-1" />
            <Btn onClick={pickImage} title="Upload image" tid="image">{uploading ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}</Btn>
            <Btn onClick={addVideo} title="Embed video" tid="video"><Video size={16} /></Btn>
          </>
        )}
        <Btn onClick={() => exec("removeFormat")} title="Clear formatting" tid="clear"><Eraser size={16} /></Btn>
        {enableBlocks && (
          <>
            <span className="w-px h-5 bg-[#E5E0D8] mx-1" />
            <Btn onClick={() => setShowBlocks((s) => !s)} title="Reusable blocks" tid="blocks"><LayoutTemplate size={16} /></Btn>
          </>
        )}
      </div>
      {enableBlocks && showBlocks && (
        <div className="border-b border-[#E5E0D8] bg-white px-3 py-2.5" data-testid={`${testid}-blocks-panel`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-[#144F75]">Reusable blocks</span>
            <div className="flex items-center gap-2">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={saveAsBlock} data-testid={`${testid}-save-block`} className="text-xs text-[#144F75] inline-flex items-center gap-1 hover:underline"><Plus size={12} /> Save current as block</button>
              <button type="button" onClick={() => setShowBlocks(false)} className="text-[#8FA89B] hover:text-[#144F75]"><X size={14} /></button>
            </div>
          </div>
          {blocksLoading ? <p className="text-xs text-[#8FA89B]"><Loader2 size={12} className="animate-spin inline" /> Loading…</p>
            : blocksError ? <p className="text-xs text-[#8FA89B]">Reusable blocks are available to admins.</p>
            : blocks.length === 0 ? <p className="text-xs text-[#8FA89B]">No saved blocks yet. Format some content and click “Save current as block”.</p>
            : (
              <div className="flex flex-wrap gap-1.5">
                {blocks.map((b) => (
                  <span key={b.id} className="inline-flex items-center gap-1 rounded-full bg-[#F1EEE8] pl-3 pr-1 py-1 text-xs">
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertBlock(b)} data-testid={`${testid}-block-${b.id}`} className="text-[#1A242B] hover:text-[#144F75]">{b.name}</button>
                    <button type="button" onClick={() => deleteBlock(b.id)} title="Delete block" className="grid place-items-center h-5 w-5 rounded-full text-[#8FA89B] hover:text-red-600 hover:bg-red-50"><Trash2 size={11} /></button>
                  </span>
                ))}
              </div>
            )}
        </div>
      )}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => { dirty.current = true; emit(); }}
        onBlur={emit}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        data-testid={`${testid}-area`}
        className="rte-area px-4 py-3 text-[#1A242B] leading-relaxed focus:outline-none"
        style={{ minHeight }}
      />
      <input ref={fileRef} type="file" accept="image/*" onChange={onImage} className="hidden" />
    </div>
  );
}
